import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'fileInput',
    'generateButton',
    'avatarCount',
    'avatarStatus',
    'avatarStatusMessage',
    'form',
    'usernameInput',
    'usernameHint',
    'usernameStatus',
    'bioInput',
    'bioCounter'
  ]

  connect() {
    this.usernameCheckTimeout = null
  }

  disconnect() {
    if (this.usernameCheckTimeout) {
      clearTimeout(this.usernameCheckTimeout)
    }
  }

  async uploadAvatar(event) {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!validTypes.includes(file.type)) {
      this.showAvatarStatus('error', 'Invalid file type. Please upload JPEG, PNG, GIF, or WEBP')
      return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.showAvatarStatus('error', 'File too large. Maximum size is 5MB')
      return
    }

    this.showAvatarStatus('loading', 'Uploading avatar...')

    const formData = new FormData()
    formData.append('avatar', file)

    try {
      const response = await fetch('/dashboard/profile/upload_avatar', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken
        },
        body: formData
      })

      const data = await response.json()

      if (response.ok) {
        this.showAvatarStatus('success', 'Avatar uploaded successfully!')
        this.updateAvatarPreview(data.avatar_url)
        // Clear file input
        this.fileInputTarget.value = ''
      } else {
        this.showAvatarStatus('error', data.error || 'Upload failed')
      }
    } catch (error) {
      this.showAvatarStatus('error', 'Upload failed. Please try again.')
      console.error('Avatar upload error:', error)
    }
  }

  async generateAvatar() {
    this.generateButtonTarget.disabled = true
    this.showAvatarStatus('loading', 'Generating AI avatar... This may take a moment.')

    try {
      const response = await fetch('/dashboard/profile/generate_avatar', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (response.ok) {
        this.showAvatarStatus('success', 'AI avatar generated successfully!')
        this.updateAvatarPreview(data.avatar_url)
        this.avatarCountTarget.textContent = data.remaining

        if (data.remaining <= 0) {
          this.generateButtonTarget.disabled = true
        } else {
          this.generateButtonTarget.disabled = false
        }
      } else {
        this.showAvatarStatus('error', data.error || 'Generation failed')
        this.generateButtonTarget.disabled = false
      }
    } catch (error) {
      this.showAvatarStatus('error', 'Generation failed. Please try again.')
      this.generateButtonTarget.disabled = false
      console.error('Avatar generation error:', error)
    }
  }

  checkUsername() {
    // Debounce the check
    if (this.usernameCheckTimeout) {
      clearTimeout(this.usernameCheckTimeout)
    }

    const username = this.usernameInputTarget.value.trim().toLowerCase()

    if (username.length === 0) {
      this.hideUsernameStatus()
      return
    }

    if (username.length < 3) {
      this.showUsernameStatus('error', 'Username must be at least 3 characters')
      return
    }

    this.usernameCheckTimeout = setTimeout(async() => {
      await this.performUsernameCheck(username)
    }, 300)
  }

  async performUsernameCheck(username) {
    this.showUsernameStatus('loading', 'Checking availability...')

    try {
      const response = await fetch(`/dashboard/profile/check_username?username=${encodeURIComponent(username)}`, {
        headers: {
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (data.available) {
        this.showUsernameStatus('success', data.message || 'Username is available')
      } else {
        this.showUsernameStatus('error', data.error || 'Username is not available')
      }
    } catch (error) {
      this.hideUsernameStatus()
      console.error('Username check error:', error)
    }
  }

  updateBioCounter() {
    const remaining = 500 - (this.bioInputTarget.value.length || 0)
    this.bioCounterTarget.textContent = remaining

    if (remaining < 0) {
      this.bioCounterTarget.classList.add('text-red-500')
      this.bioCounterTarget.classList.remove('text-gray-500')
    } else if (remaining < 50) {
      this.bioCounterTarget.classList.add('text-amber-500')
      this.bioCounterTarget.classList.remove('text-gray-500', 'text-red-500')
    } else {
      this.bioCounterTarget.classList.remove('text-amber-500', 'text-red-500')
    }
  }

  showAvatarStatus(type, message) {
    const statusClasses = ['hidden', 'bg-green-50', 'bg-red-50', 'bg-blue-50']
    const borderClasses = ['border-green-500', 'border-red-500', 'border-blue-500']
    this.avatarStatusTarget.classList.remove(...statusClasses, ...borderClasses)
    this.avatarStatusMessageTarget.classList.remove('text-green-700', 'text-red-700', 'text-blue-700')

    switch (type) {
      case 'success':
        this.avatarStatusTarget.classList.add('bg-green-50', 'border-green-500')
        this.avatarStatusMessageTarget.classList.add('text-green-700')
        break
      case 'error':
        this.avatarStatusTarget.classList.add('bg-red-50', 'border-red-500')
        this.avatarStatusMessageTarget.classList.add('text-red-700')
        break
      case 'loading':
        this.avatarStatusTarget.classList.add('bg-blue-50', 'border-blue-500')
        this.avatarStatusMessageTarget.classList.add('text-blue-700')
        break
    }

    this.avatarStatusMessageTarget.textContent = message

    // Auto-hide success/error after 5 seconds
    if (type !== 'loading') {
      setTimeout(() => {
        this.avatarStatusTarget.classList.add('hidden')
      }, 5000)
    }
  }

  showUsernameStatus(type, message) {
    this.usernameStatusTarget.classList.remove('hidden', 'text-green-600', 'text-red-600', 'text-gray-500')

    switch (type) {
      case 'success':
        this.usernameStatusTarget.classList.add('text-green-600')
        break
      case 'error':
        this.usernameStatusTarget.classList.add('text-red-600')
        break
      case 'loading':
        this.usernameStatusTarget.classList.add('text-gray-500')
        break
    }

    this.usernameStatusTarget.textContent = message
  }

  hideUsernameStatus() {
    this.usernameStatusTarget.classList.add('hidden')
    this.usernameStatusTarget.textContent = ''
  }

  updateAvatarPreview(url) {
    const avatarElement = document.getElementById('current-avatar')
    if (avatarElement) {
      if (avatarElement.tagName === 'IMG') {
        avatarElement.src = url
      } else {
        // Replace div with img
        const img = document.createElement('img')
        img.src = url
        img.className = 'w-24 h-24 rounded-full object-cover border-2 border-gray-900'
        img.id = 'current-avatar'
        avatarElement.replaceWith(img)
      }
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
